<template>
    <div>
        <b-row>
            <b-col md="12" class="mb-3">
                <span class="feedbacks-tooltip">
                    Topics
                    <b-btn variant="ghost" v-b-popover.hover="'Set a list of topics your loyalty member can choose when sending a feedback through their app'" title="">
                        <i class="ic-tooltip" id="topics"></i>
                    </b-btn>
                </span>
            </b-col>
            <b-col sm="6" md="3" class="mb-3 mb-sm-3 mb-md-0 mb-lg-0">
                <div class="filter-wrapper">
                    <b-input-group prepend=" ">
                        <b-form-input placeholder="Search" v-model="filter.keyword"></b-form-input>
                        <b-dropdown text="Advanced" variant="primary" slot="append" v-model="filter.field">
                            <template v-for="(field,index) in searchFields">
                                <b-dropdown-item @click="searchOption(field.key)" :key="index">
                                    {{ field.label }}
                                    <i v-if="filter.field == field.key" class="ml-1"> 
                                        <img width="15" height="15" src="@/assets/images/checked-symbol.svg" alt="" class="mr-1">
                                    </i>
                                </b-dropdown-item>
                            </template>
                        </b-dropdown>
                    </b-input-group>
                </div>
            </b-col>
            <b-col sm="5" md="9" class="text-left text-md-right">
                <b-button
                    variant="primary new-button"
                    v-b-modal="'new-topic'">
                    + New Topic
                </b-button>
            </b-col>
        </b-row>
        <div class="mt-3">
            <div id="topics" class="data-container">
                <b-table 
                    :items="list"
                    :fields="topics_fields"
                    :current-page="page"
                    responsive>
                    <template slot="description" slot-scope="data">
                        <div class="description-container">
                            {{ data.value }}
                        </div>
                    </template>
                    <template slot="visibility" slot-scope="data">
                        <switches
                            :value="data.item.visibility"
                            theme="custom"
                            color="primary"
                            type-bold="true"
                            text-enabled="On"
                            text-disabled="Off">
                        </switches>
                    </template>
                    <template slot="action" slot-scope="data">
                        <b-button
                            variant="ghost btn-table-edit"
                            v-b-modal="'edit-topic'"
                            @click="getTopic(data.item.uuid)">
                        </b-button>
                        <b-button
                            variant="ghost btn-table-delete"
                            v-b-modal="'delete-topic'"
                            @click="confirmDelete(data.item.uuid)">
                        </b-button>
                    </template>
                </b-table>
            </div>
            <div>
                <b-row>
                    <b-col>
                        <div class="mt-1">
                            <span class="pagination-indicator">{{ startList }}-{{ endList }} of {{ maxPage }} per page</span>
                            <b-pagination 
                                hide-goto-end-buttons 
                                align="left" 
                                size="md" 
                                :total-rows="maxPage * list.length"
                                :limit="3"
                                :per-page="list.length"
                                @change="paginate"
                                :value="page"
                                class="custom-pagination-arrow-only mb-0 ml-2">
                                </b-pagination>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </div>
        <b-modal
            id="new-topic"
            size="lg"
            title="New Topic"
            ref="createModal"
            @shown="clearForm">
            <b-container fluid>
                <div class="modal-form">
                    <b-form>
                        <b-row no-gutters>
                            <b-col sm="12" md="3">
                                <h6 class="main-label">Topic Details</h6>
                            </b-col>
                            <b-col>
                                <b-form-group
                                    label="Title"
                                    label-for="title"
                                    :state="isValid('title')"
                                    :invalid-feedback="errors.title"
                                    class="secondary-label">
                                    <b-form-input
                                        type="text"
                                        v-model="form.title"
                                        placeholder="Enter Title"
                                        required
                                        maxlength="30">
                                    </b-form-input>
                                </b-form-group>
                                <b-form-group
                                    :label="'Description (Optional)' + topicDescriptionLabel"
                                    label-for="description"
                                    :state="isValid('description')"
                                    :invalid-feedback="errors.description"
                                    class="secondary-label">
                                    <b-form-textarea
                                        rows="3"
                                        v-model="form.description"
                                        :maxlength="form.maxTopicDescription"
                                        placeholder="Enter Description">
                                    </b-form-textarea>
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row class="mb-2">
                            <b-col>
                                <hr class="form-divider">
                            </b-col>
                        </b-row>
                        <b-row no-gutters>
                            <b-col sm="12" md="3">
                                <h6 class="main-label">Visibility</h6>
                            </b-col>
                            <b-col>
                                <b-form-group
                                    label="Publish in App"
                                    label-for="visibility"
                                    class="secondary-label">
                                    <switches
                                        v-model="form.visibility"
                                        theme="custom"
                                        color="primary"
                                        type-bold="true"
                                        text-enabled="On"
                                        text-disabled="Off">
                                    </switches>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-form>
                </div>
            </b-container>
            <div slot="modal-footer">
                <b-btn variant="ghost" v-bind:disabled="loading" @click="handleCancel('new-topic')" class="mr-1 btn-cancel">
                    Cancel
                </b-btn>
                <b-btn variant="primary" v-bind:disabled="loading" class="ml-1" @click="createTopic">
                    <template v-if="loading">
                        Saving...
                    </template>
                    <template v-else>
                        Save
                    </template>
                </b-btn>
            </div>
        </b-modal>
        <b-modal
            id="edit-topic"
            size="lg"
            title="Edit Topic"
            ref="updateModal"
            @shown="clearForm">
            <b-container fluid>
                <div class="modal-form">
                    <b-form>
                        <b-row no-gutters>
                            <b-col sm="12" md="3">
                                <h6 class="main-label">Topic Details</h6>
                            </b-col>
                            <b-col>
                                <b-form-group
                                    label="Title"
                                    label-for="title"
                                    :state="isValid('title')"
                                    :invalid-feedback="errors.title"
                                    class="secondary-label">
                                    <b-form-input
                                        type="text"
                                        placeholder="Enter Title"
                                        v-model="topic.title"
                                        required>
                                    </b-form-input>
                                </b-form-group>
                                <b-form-group
                                    :label="'Description ' + editTopicDescriptionLabel"
                                    label-for="description"
                                    class="secondary-label">
                                    <b-form-textarea
                                        rows="3"
                                        placeholder="Enter Description"
                                        :maxlength="form.maxTopicDescription"
                                        v-model="topic.description">
                                    </b-form-textarea>
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-row class="mb-2">
                            <b-col>
                                <hr class="form-divider">
                            </b-col>
                        </b-row>
                        <b-row no-gutters>
                            <b-col sm="12" md="3">
                                <h6 class="main-label">Visibility</h6>
                            </b-col>
                            <b-col>
                                <b-form-group
                                    label="Publish in App"
                                    label-for="visibility"
                                    class="secondary-label">
                                    <switches
                                        v-model="topic.visibility"
                                        theme="custom"
                                        color="primary"
                                        type-bold="true"
                                        text-enabled="On"
                                        text-disabled="Off">
                                    </switches>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-form>
                </div>
            </b-container>
            <div slot="modal-footer">
                <b-btn variant="ghost" v-bind:disabled="loading" @click="handleCancel('edit-topic')" class="mr-1 btn-cancel">
                    Cancel
                </b-btn>
                <b-btn variant="primary" v-bind:disabled="loading" class="ml-1" @click="updateTopic">
                    <template v-if="loading">
                        Updating...
                    </template>
                    <template v-else>
                        Update
                    </template>
                </b-btn>
            </div>
        </b-modal>
        <b-modal
            id="delete-topic"
            title="Delete Topic"
            ref="deleteModal"
            centered>
            <p class="text-center">Are you sure you want to delete this topic?</p>
            <div slot="modal-footer">
                <b-btn variant="ghost" v-bind:disabled="loading" @click="handleCancel('delete-topic')" class="mr-1 btn-cancel">
                    Cancel
                </b-btn>
                <b-btn variant="primary" v-bind:disabled="loading" class="ml-1" @click="deleteTopic">
                    <template v-if="loading">
                        Deleting...
                    </template>
                    <template v-else>
                        Yes, Delete
                    </template>
                </b-btn>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Switches from "vue-switches";
import { mapState } from "vuex";

export default {
    name: "customer-topic",
    components: {
        Switches
    },
    data() {
        return {
            timeout: 500,
            filter: {
                keyword: null,
                field: "all"
            },
            searchFields: [
                {
                    key: "all",
                    label: "All"
                },
                {
                    key: "title",
                    label: "Title"
                },
                {
                    key: "description",
                    label: "Description"
                }
            ],
            topics_fields: [
                {
                    key: "title",
                    sortable: true
                },
                {
                    key: "description",
                    sortable: true
                },
                {
                    key: "visibility",
                    sortable: false
                },
                {
                    key: "action",
                    sortable: false,
                    variant: "action"
                }
            ],
            form: {
                title: "",
                description: "",
                visibility: false,
                maxTopicDescription: 200
            }
        };
    },
    created: function() {
        this.$store.dispatch("topics/getFirstPage");
    },
    computed: {
        ...mapState("topics", [
            "error",
            "errors",
            "uuid",
            "loading",
            "view",
            "list",
            "page",
            "perPage",
            "maxPage",
            "nextPage",
            "prevPage",
            "startList",
            "endList",
            "message",
            "topic"
        ]),
        filterKeyword() {
            return this.filter.keyword;
        },
        topicDescriptionLabel: {
            get: function() {
                if (this.form.description != null) {
                    return (
                        this.form.description.length +
                        " / " +
                        this.form.maxTopicDescription
                    );
                }
                return "0 / " + this.form.maxTopicDescription;
            }
        },
        editTopicDescriptionLabel: {
            get: function() {
                if (this.topic.description != null) {
                    return (
                        this.topic.description.length +
                        " / " +
                        this.form.maxTopicDescription
                    );
                }
                return "0 / " + this.form.maxTopicDescription;
            }
        }
    },
    methods: {
        isValid(field) {
            return this.errors[field] ? false : true;
        },
        paginate(page) {
            this.$store.commit("topics/setPage", page);
            this.$store.dispatch("topics/getList");
        },
        searchOption(filter) {
            this.filter.field = filter;
            this.doSearch();
        },
        doSearch: function() {
            let _this = this;

            setTimeout(function() {
                _this.$store.commit("topics/setSearchDefault");
                _this.$store.commit("topics/setSearchFilter", _this.filter);
                _this.$store.dispatch("topics/getFirstPage");
            }, this.timeout);
        },
        clearForm() {
            this.form.title = "";
            this.form.description = "";
            this.form.visibility = "";
            this.$store.commit("topics/setErrorDefault");
        },
        createTopic(evt) {
            evt.preventDefault();
            let _this = this;

            this.$store.commit("topics/setTopic", this.form);
            this.$store.dispatch("topics/createTopic").then(function() {
                if (_this.error == false) {
                    _this.$refs.createModal.hide();
                    _this.$store.dispatch("topics/getList");
                }
            });
        },
        hideModal(modal) {
            this.$root.$emit("bv::hide::modal", modal);
        },
        handleCancel(modal) {
            this.hideModal(modal);
        },
        getTopic(uuid) {
            this.$root.$emit("bv::show::modal", "edit-topic");
            this.$store.commit("topics/setUuid", uuid);
            this.$store.dispatch("topics/getTopic");
        },
        updateTopic(evt) {
            evt.preventDefault();
            let _this = this;

            this.$store.dispatch("topics/updateTopic").then(function() {
                if (_this.error == false) {
                    _this.$refs.updateModal.hide();
                    _this.$store.dispatch("topics/getList");
                }
            });
        },
        confirmDelete(uuid) {
            this.$store.commit("topics/setUuid", uuid);
            this.$root.$emit("bv::show::modal", "delete-topic");
        },
        deleteTopic() {
            let _this = this;

            this.$store.dispatch("topics/deleteTopic").then(function() {
                if (_this.error == false) {
                    _this.$refs.deleteModal.hide();
                    _this.$store.dispatch("topics/getList");
                }
            });
        }
    },
    watch: {
        filterKeyword() {
            this.doSearch();
        }
    }
};
</script>
